<template>
<div
    :class='{
        "fw-input__wrapper--with-icon": iconLeft || iconLeftTop,
        "fw-input__wrapper--type-password": isTypePassword,
        "fw-input__wrapper--with-left-block": $slots.left,
        "fw-input__wrapper--with-right-block": $slots.right,
        "fw-input__wrapper--search": isSearchType,
        "fw-input__wrapper--disabled": disabled,
        "h-flex fw-input__wrapper--with-prefix": isPhoneWithISOCode,
    }'
    class='fw-input__wrapper'>
    <div v-if='$slots.left'
         class='fw-input__left_block'>
        <slot name='left'></slot>
    </div>
    <FwIcon
        v-if='iconLeft'
        :icon='iconLeft'
        :size='iconLeftSize'
        :color='error ? "#cf2e2e" : "#b1bbca"'
        class='fw-input__icon--left fw-input__icon'
        @click.native='setInputFocus'>
    </FwIcon>
    <div v-if='isPhoneWithISOCode'
         style='max-width: 100px'
         class='fw-select-base h-width-100p'>
        <FwSelect
            class='fw-select-white'
            phoneWithCodeStartValue=''
            type='phone-with-code'
            :phoneNumber='phoneNumber'
            propsPlaceholder='+33'
            :locales='locales'
            :canNotDelete='true'
            :tryToUseSubstring='tryToUseSubstring'
            @substrPhone='substrPhone'
            @select='onSelect'></FwSelect>
    </div>

    <div class='fw-input__inner'
         :style='{ ...inputWidth ? { width: inputWidth } : {} }'>
        <component
            :is='componentType === "textarea" ? componentType : "input"'
            ref='input'
            :name='name'
            :class='[
                "fw-input",
                `fw-input--${inputStyle}`,
                {
                    "fw-input--error": error,
                }
            ]'
            :style='{
                fontWeight: fontWeight,
                paddingRight: inputPaddingRight,
                pointerEvents: inputPointerEvents,
            }'
            :type='localType'
            :value.prop='valueModel'
            :placeholder='placeholder'
            :disabled='disabled || (isPhoneWithISOCode && !currentPhonePrefix)'
            v-bind='$attrs'
            @blur='$emit("blur")'
            @keydown='keyDownMethod'
            @input='inputHandler'>
        </component>
        <button
            v-if='buttonRight'
            class='fw-input__inner--button'
            :disabled='buttonDisabled'
            @click='$emit(`addTextFromInput`, valueModel)'>
            {{ buttonRight }}
        </button>

        <FwIcon
            v-if='iconRight && isSearchType && valueModel'
            icon='close'
            :size='iconSize'
            color='#b1bbca'
            class='fw-input__icon--right fw-input__icon'
            @click.native='clearSearch'>
        </FwIcon>
        <FwIcon
            v-else-if='iconRight'
            :icon='iconRight'
            :size='iconSize'
            :color='error ? "#cf2e2e" : "#b1bbca"'
            class='fw-input__icon--right fw-input__icon'
            @click.native.stop='setInputFocus'>
        </FwIcon>
        <FwIcon
            v-if='isTypePassword'
            :icon='isLocalTypePassword ? "eye-cross" : "eye"'
            size='25'
            color='#b1bbca'
            class='fw-input__icon--right fw-input__icon'
            @click.native='toggleLocalType(true)'>
        </FwIcon>
    </div>

    <div v-if='$slots.right && isTypePassword'
         class='fw-input__right_block'
         @click='toggleLocalType'>
        <slot name='right'></slot>
    </div>
    <div v-else-if='$slots.right'
         class='fw-input__right_block'>
        <slot name='right'></slot>
    </div>
    <div v-if='error'
         class='fw-input__error-text'>
        {{ error }}
    </div>
</div>
</template>

<script lang='ts'>
import Vue from 'vue';
import { Component, Prop, PropSync } from 'vue-property-decorator';

const INPUT_TYPE_TEXT : string = 'text';
const INPUT_TYPE_PASSWORD : string = 'password';
const INPUT_TYPE_EMAIL : string = 'email';
const INPUT_TYPE_TEL : string = 'tel';
const INPUT_TYPE_SEARCH : string = 'search';
const INPUT_TYPE_NUMBER : string = 'number';

const INPUT_TYPES: Array<string> = [
    INPUT_TYPE_TEXT,
    INPUT_TYPE_PASSWORD,
    INPUT_TYPE_EMAIL,
    INPUT_TYPE_TEL,
    INPUT_TYPE_SEARCH,
    INPUT_TYPE_NUMBER,
];

const INPUT_STYLE_BASE: string = 'base';
const INPUT_STYLE_TRANSPARENT: string = 'transparent';
const INPUT_STYLE_BASE_LIGHT: string = 'base-light';
const INPUT_STYLE_WHITE: string = 'white';
const INPUT_STYLE_WHITE_BIGGER: string = 'white-bigger';
const INPUT_STYLE_WHITE_BIGGER_SHADOW: string = 'white-bigger-shadow';
const INPUT_STYLE_BORDER_BIGGER_SHADOW: string = 'border-bigger-shadow';
const INPUT_STYLE_WHITE_BIGGER_SEARCH: string = 'white-bigger-search';
const INPUT_STYLE_GRAY_BIGGER: string = 'gray-bigger';
const INPUT_STYLE_GRAY: string = 'gray';
const INPUT_STYLE_TEXTAREA: string = 'textarea';
const INPUT_STYLE_MESSENGER_TEXTAREA: string = 'messenger-textarea';
const INPUT_STYLE_SEARCH: string = 'search';

const INPUT_STYLES: Array<string> = [
    INPUT_STYLE_TRANSPARENT,
    INPUT_STYLE_BASE,
    INPUT_STYLE_BASE_LIGHT,
    INPUT_STYLE_WHITE,
    INPUT_STYLE_WHITE_BIGGER,
    INPUT_STYLE_WHITE_BIGGER_SHADOW,
    INPUT_STYLE_BORDER_BIGGER_SHADOW,
    INPUT_STYLE_WHITE_BIGGER_SEARCH,
    INPUT_STYLE_GRAY_BIGGER,
    INPUT_STYLE_GRAY,
    INPUT_STYLE_TEXTAREA,
    INPUT_STYLE_SEARCH,
    INPUT_STYLE_MESSENGER_TEXTAREA,
];

@Component({
    inheritAttrs: false,
    model: {
        event: 'update',
    },
})
export default class FwInput extends Vue {
    @Prop({ type: String, default: null }) readonly iconLeft!: string | null;
    @Prop({ type: String, default: null }) readonly iconLeftTop!: string | null;
    @Prop({ type: String, default: null }) readonly iconRight!: string | null;
    @Prop({ type: String, default: null }) readonly placeholder!: string | null;
    @Prop({ type: String, default: null }) readonly buttonRight!: string | null;
    @Prop({ type: String, default: null }) readonly error!: string | null;
    @Prop({ type: String, default: '18' }) readonly iconSize!: string | number;
    @Prop({ type: String, default: '25' }) readonly iconLeftSize!: string | number;
    @Prop({ type: String, default: null }) readonly inputPaddingRight!: string | number;
    @Prop({ type: String, default: 'auto' }) readonly inputPointerEvents!: string;
    @Prop({ type: String, default: '400' }) readonly fontWeight!: string | number;
    @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;
    @Prop({ type: Boolean, default: false }) readonly buttonDisabled!: boolean;
    @Prop({ type: Boolean, default: false }) readonly isPhoneWithISOCode!: boolean;
    @Prop({ type: Boolean, default: false }) readonly tryToUseSubstring!: boolean;
    @Prop({ type: String, default: 'input' }) readonly componentType!: string;
    @Prop({ type: String, default: null }) readonly inputWidth!: string;
    @Prop({ type: Function, default: null }) readonly propsKeyDownMethod!: Function;
    @Prop({ type: String }) readonly name!: string;
    @Prop({ type: Array<String> }) readonly locales!: Array<string>;
    @Prop({ type: String, default: '' }) readonly phoneNumber!: string;
    @PropSync('value', { type: [String, Number], required: true }) valueModel!: string | number;
    @Prop({ type: String, default: INPUT_TYPE_TEXT, validator: (type: string) => INPUT_TYPES.includes(type) })
    type!: string;
    @Prop({ type: String, default: INPUT_STYLE_BASE, validator: (type: string) => INPUT_STYLES.includes(type) })
    inputStyle!: string;

    localType: string = '';
    currentPhonePrefix: string = '';

    get isTypePassword(): boolean {
        return this.type === INPUT_TYPE_PASSWORD;
    }

    get isLocalTypePassword(): boolean {
        return this.localType === INPUT_TYPE_PASSWORD;
    }

    get isTypeText(): boolean {
        return this.type === INPUT_TYPE_TEXT;
    }

    get isSearchType() {
        return this.type === INPUT_TYPE_SEARCH;
    }

    setInputFocus(): void {
        (this.$refs.input as HTMLInputElement).focus();
    }

    toggleLocalType(noFocus: boolean): void {
        if (this.localType === INPUT_TYPE_PASSWORD) {
            this.localType = INPUT_TYPE_TEXT;
        } else if (this.localType === INPUT_TYPE_TEXT) {
            this.localType = INPUT_TYPE_PASSWORD;
        }
        if (!noFocus) {
            this.setInputFocus();
        }
    }

    inputHandler($event: Event) {
        this.valueModel = ($event.currentTarget as HTMLInputElement).value;
        this.$emit('input');
        this.$nextTick(() => {
            if (this.isPhoneWithISOCode) {
                this.$emit('takePhoneWithPrefix', this.currentPhonePrefix + this.valueModel);
            }
        });
    }

    keyDownMethod($event: Event) {
        if (this.propsKeyDownMethod) {
            this.propsKeyDownMethod($event, this.isPhoneWithISOCode);
        }
        this.$emit('onKeydown');
    }

    clearSearch() {
        this.valueModel = ``;
        this.$emit('input');
    }

    onSelect(country: any) {
        this.currentPhonePrefix = country.prefix;
        this.$emit('updatePrefix', country.prefix);
        this.$emit('takePhoneWithPrefix', country.prefix + this.valueModel);
    }

    substrPhone(phone: string, prefix: string) {
        this.currentPhonePrefix = prefix;
        this.$emit('substrPhone', phone, prefix);
        this.$emit('takePhoneWithPrefix', prefix + phone);
    }

    created() {
        this.localType = this.isSearchType ? INPUT_TYPE_TEXT : this.type;
    }
};
</script>

<style lang='sass'>
$base-input-color: $light-grey

.fw-input
    border-radius: 15px
    height: $input-h
    display: flex
    align-content: center
    padding: 0 20px
    outline: none
    width: 100%
    transition: background-color 0.15s ease-in-out
    color: $dark-blue
    font-size: 16px
    text-overflow: ellipsis
    overflow: hidden

    &__inner
        position: relative
        display: flex
        justify-content: center
        align-items: center
        &--button
            cursor: pointer
            font-size: 14px
            font-weight: 700
            color: $aqua-marine
            position: absolute
            outline: none
            background: transparent
            border: none
            height: 100%
            width: 100px
            right: 0
            margin: 0
    &__error-text
        color: $main-red
        font-size: 14px
        position: absolute
        left: 0
        top: 100%
        margin-top: 3px

    &--error
        border: 1px solid $main-red
        background-color: transparent

    &:not([disabled])
        &:hover,
        &:active,
        &:focus
            background-color: white

    &[disabled]
        user-select: none
        cursor: not-allowed
        background-color: #fff

    &::placeholder
        color: $main-gray
        font-weight: 300
        font-size: 16px

    &::-webkit-input-placeholder
        color: $main-gray
        font-weight: 300
        font-size: 16px

    &::-moz-placeholder
        color: $main-gray
        font-weight: 300
        font-size: 16px

    &:-ms-input-placeholder
        color: $main-gray
        font-weight: 300
        font-size: 16px

    &:-moz-placeholder
        color: $main-gray
        font-weight: 300

    &__icon
        position: absolute
        width: 60px
        display: flex
        height: 100%
        align-items: center
        justify-content: center
        top: 0
        z-index: 1

        &--left
            left: 0

        &--right
            cursor: pointer
            right: 0
        &--left-top
            cursor: pointer
            left: 0
            display: flex
            justify-content: center
            align-items: flex-start
            top: 20px

    &--base
        background-color: $middle-gray
        border: 2px solid $middle-gray

    &--white
        background-color: #fff
        box-shadow: $big-box-shadow
        border: none

    &--white-bigger
        border-radius: 10px
        min-height: 60px
        background-color: #fff
        border: none
    &--border-bigger-shadow
        border: 1px solid #bdbdbd61!important
    &--white-bigger-shadow, &--border-bigger-shadow
        text-overflow: ellipsis
        min-height: 60px
        border: none
        border-radius: 10px
        overflow: hidden
        white-space: nowrap
        font-weight: 500!important
        box-shadow: 0 30px 11px -17px rgba(33,63,107,0.05)
        &::placeholder
            color: rgba(33,63,107,0.5)
            font-weight: 500
    &--white-bigger-search
        text-overflow: ellipsis
        overflow: hidden
        white-space: nowrap
        font-weight: 500!important
        border-radius: 10px
        border: none
        box-shadow: 0 30px 11px -17px rgba(33,63,107,0.05)
        &::placeholder
            font-size: 14px
            color: rgba(33,63,107,0.5)
            font-weight: 400

    &--gray-bigger
        border-radius: 10px
        min-height: 60px
        background-color: #F0F2F5
        border: none

    &--gray
        border-radius: 10px
        min-height: 50px
        background-color: #F0F2F5
        border: none

        &:hover
            background-color: $light-gray !important

    &--base-light
        background-color: $light-gray
        border: 2px solid $light-gray
    &--search
        border: 2px solid #F8F9FA
        height: 50px
        border-radius: 10px
        background-color: #F8F9FA

    &--transparent
        opacity: 0.3
        text-align: center
        font-size: 30px
        font-weight: bold
        background-color: transparent
        border: none
        &::placeholder
            font-size: 30px
            font-weight: bold
        &:not([disabled])
            &:hover,
            &:active,
            &:focus
                background-color: transparent
    &--textarea
        line-height: 20px
        min-height: 75px
        resize: none
        padding: 17px 20px 10px
        border-radius: 10px
        border: none
        background-color: #FFFFFF
        box-shadow: 0 30px 11px -17px rgba(33,63,107,0.02)
    &--messenger-textarea
        resize: none
        font-size: 16px
        font-weight: 500
        line-height: 19px
        height: 121px
        border: none
        padding: 20px 30px
        border-radius: 20px
        background-color: rgba(33,63,107,0.05)
        &:not([disabled])
            &:hover,
            &:active,
            &:focus
                background-color: rgba(33,63,107,0.1)
    &__wrapper
        position: relative
        &--with-icon
            .fw-input
                padding-left: 60px

        &--type-password
            .fw-input
                padding-right: 60px

        &--disabled
            opacity: 0.5

        &--with-prefix
            .fw-input
                padding-left: 0
                border-top-left-radius: 0!important
                border-bottom-left-radius: 0!important
            .multiselect__tags
                border-top-right-radius: 0!important
                border-bottom-right-radius: 0!important

.fw-input__wrapper--with-left-block
    .fw-input
      padding-left: 130px

.fw-input__wrapper--with-right-block
    .fw-input__inner
        width: 100%

.fw-input__wrapper--with-left-block, .fw-input__wrapper--with-right-block
    display: flex
    align-items: center
    justify-content: center
    .multiselect:after
        display: none !important
    .fw-input__left_block
        position: absolute
        z-index: 1
        left: 0
        .multiselect__option
            padding: 10px !important

    .fw-input__right_block
        position: absolute
        z-index: 1
        right: 0
        .multiselect__option
            padding: 10px !important

    .multiselect__tags
        display: flex
        align-items: center

        height: 50px !important
        max-height: 50px !important

    .multiselect__content
        padding: 0 10px !important
        max-height: 300px
        overflow: auto
        margin: 0 0 0 -10px !important
    .multiselect__input
        opacity: 0 !important
        max-width: 0 !important

    .multiselect__element
        height: 40px !important
        display: flex
        align-items: center

.fw-select-phone-with-code
    height: 50px
</style>

